import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Briefcase, TrendingUp, AlertCircle } from 'lucide-react';
import { dashboardAPI } from '@/utils/api';
import { toast } from 'sonner';
import LeadFunnelKanban from '@/components/admin/LeadFunnelKanban';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await dashboardAPI.getSummary();
        setStats(data);
      } catch (error) {
        toast.error('Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-blue-500'
    },
    {
      title: 'Enquiries Today',
      value: stats?.enquiries_today || 0,
      icon: TrendingUp,
      color: 'text-green-500'
    },
    {
      title: 'Active Jobs',
      value: stats?.jobs_in_progress || 0,
      icon: Briefcase,
      color: 'text-amber-500'
    },
    {
      title: 'Emergency Alerts',
      value: stats?.emergency_today || 0,
      icon: AlertCircle,
      color: 'text-red-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">Overview of your business operations</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">{stat.title}</p>
                  <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                </div>
                <stat.icon className={`w-12 h-12 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lead Funnel Kanban */}
      <LeadFunnelKanban />

      {/* Enquiries by Service Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Enquiries by Service Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.enquiries_by_service && Object.keys(stats.enquiries_by_service).length > 0 ? (
                Object.entries(stats.enquiries_by_service).map(([serviceType, count]) => (
                  <div key={serviceType} className="flex items-center justify-between">
                    <span className="text-gray-300 capitalize">
                      {(serviceType || 'Unknown').replace(/_/g, ' ')}
                    </span>
                    <span className="text-white font-semibold">{count}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Enquiries by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.enquiries_by_source && Object.keys(stats.enquiries_by_source).length > 0 ? (
                Object.entries(stats.enquiries_by_source).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <span className="text-gray-300 capitalize">{(source || 'Unknown').replace(/_/g, ' ')}</span>
                    <span className="text-white font-semibold">{count}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;